import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { withCache } from '@ngneat/cashew';

import { API, ProductDetailPath } from '@cdba/shared/constants/api';
import {
  CostComponentSplitItem,
  CostComponentSplitType,
  Drawing,
  ReferenceType,
  ReferenceTypeIdentifier,
} from '@cdba/shared/models';
import {
  CalculationsResponse,
  PricingResponse,
} from '@cdba/shared/models/responses';
import {
  SchaefflerBomIdentifier,
  SchaefflerBomItem,
} from '@cdba/shared/models/schaeffler-bom-item.model';
import {
  VitescoBomIdentifier,
  VitescoBomItem,
} from '@cdba/shared/models/vitesco-bom-item.model';
import { LocalStorageService } from '@cdba/shared/services';
import {
  defineSchaefflerBomTreeForAgGrid,
  defineVitescoBomTreeForAgGrid,
} from '@cdba/shared/utils/ag-grid-tree.util';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailService {
  private readonly httpClient = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly PARAM_LANGUAGE = 'language';

  private readonly PARAM_MATERIAL_NUMBER = 'material_number';
  private readonly PARAM_PLANT = 'plant';

  private readonly PARAM_COSTING_DATE = 'costing_date';
  private readonly PARAM_COSTING_NUMBER = 'costing_number';
  private readonly PARAM_COSTING_TYPE = 'costing_type';
  private readonly PARAM_VERSION = 'version';
  private readonly PARAM_ENTERED_MANUALLY = 'entered_manually';
  private readonly PARAM_REFERENCE_OBJECT = 'reference_object';
  private readonly PARAM_VALUATION_VARIANT = 'valuation_variant';

  getDetails(item: ReferenceTypeIdentifier): Observable<ReferenceType> {
    const params: HttpParams = new HttpParams()
      .set(this.PARAM_MATERIAL_NUMBER, item.materialNumber)
      .set(this.PARAM_PLANT, item.plant)
      .set(
        this.PARAM_LANGUAGE,
        this.localStorageService.getItem<string>('language', false)
      );

    const path = `${API.v1}/${ProductDetailPath.Detail}`;

    return this.httpClient.get<ReferenceType>(path, {
      params,
      context: withCache(),
    });
  }

  getCalculations(
    materialNumber: string,
    plant: string
  ): Observable<CalculationsResponse> {
    const params = new HttpParams()
      .set(this.PARAM_MATERIAL_NUMBER, materialNumber)
      .set(this.PARAM_PLANT, plant);

    return this.httpClient.get<CalculationsResponse>(
      `${API.v1}/${ProductDetailPath.Calculations}`,
      {
        params,
        context: withCache(),
      }
    );
  }

  getVitescoBom(
    bomIdentifier: VitescoBomIdentifier
  ): Observable<VitescoBomItem[]> {
    const path = `${API.v1}/${ProductDetailPath.VitescoBom}`;

    const params: HttpParams = new HttpParams()
      .set(this.PARAM_MATERIAL_NUMBER, bomIdentifier.materialNumber)
      .set(this.PARAM_PLANT, bomIdentifier.plant);

    return this.httpClient
      .get<VitescoBomItem[]>(path, { params })
      .pipe(
        map((items: VitescoBomItem[]) => defineVitescoBomTreeForAgGrid(items))
      );
  }

  getSchaefflerBom(
    bomIdentifier: SchaefflerBomIdentifier
  ): Observable<SchaefflerBomItem[]> {
    const path = `${API.v1}/${ProductDetailPath.SchaefflerBom}`;

    const params: HttpParams = new HttpParams()
      .set(this.PARAM_COSTING_DATE, bomIdentifier.costingDate)
      .set(this.PARAM_COSTING_NUMBER, bomIdentifier.costingNumber)
      .set(this.PARAM_COSTING_TYPE, bomIdentifier.costingType)
      .set(this.PARAM_VERSION, bomIdentifier.version)
      .set(this.PARAM_ENTERED_MANUALLY, false)
      .set(this.PARAM_REFERENCE_OBJECT, bomIdentifier.referenceObject)
      .set(this.PARAM_VALUATION_VARIANT, bomIdentifier.valuationVariant);

    return this.httpClient
      .get<SchaefflerBomItem[]>(path, {
        params,
        context: withCache(),
      })
      .pipe(
        map((response: any) => response),
        map((items) =>
          items.map((item: any) => ({
            ...item,
            predecessorsInTree: [],
          }))
        ),
        map((items: SchaefflerBomItem[]) =>
          defineSchaefflerBomTreeForAgGrid(items, 0)
        )
      );
  }

  getCostComponentSplit(
    bomIdentifier: SchaefflerBomIdentifier
  ): Observable<CostComponentSplitItem[]> {
    const path = `${API.v1}/${ProductDetailPath.CostComponentSplit}`;

    const params: HttpParams = new HttpParams()
      .set(this.PARAM_COSTING_DATE, bomIdentifier.costingDate)
      .set(this.PARAM_COSTING_NUMBER, bomIdentifier.costingNumber)
      .set(this.PARAM_COSTING_TYPE, bomIdentifier.costingType)
      .set(this.PARAM_VERSION, bomIdentifier.version)
      .set(this.PARAM_ENTERED_MANUALLY, bomIdentifier.enteredManually)
      .set(this.PARAM_REFERENCE_OBJECT, bomIdentifier.referenceObject)
      .set(this.PARAM_VALUATION_VARIANT, bomIdentifier.valuationVariant);

    return this.httpClient
      .get<CostComponentSplitItem[]>(path, {
        params,
        context: withCache(),
      })
      .pipe(
        map((items) => {
          let sumTotalValue = 0;
          let sumVariableValue = 0;
          let sumFixedValue = 0;

          // only compute if split type "total" is not included in an array
          // this way we know the response is not cached
          if (
            !items.some((el) => el.splitType === CostComponentSplitType.TOTAL)
          ) {
            items.forEach((item) => {
              if (item.splitType === CostComponentSplitType.MAIN) {
                sumTotalValue += item.totalValue;
                sumVariableValue += item.variableValue;
                sumFixedValue += item.fixedValue;
              }
            });

            items.push({
              costComponent: undefined,
              description: undefined,
              splitType: CostComponentSplitType.TOTAL,
              totalValue: sumTotalValue,
              fixedValue: sumFixedValue,
              variableValue: sumVariableValue,
              currency: items[0]?.currency,
            });
          }

          return items;
        })
      );
  }

  getDrawings(materialNumber: string, plant: string): Observable<Drawing[]> {
    const params = new HttpParams()
      .set(this.PARAM_MATERIAL_NUMBER, materialNumber)
      .set(this.PARAM_PLANT, plant);

    return this.httpClient.get<Drawing[]>(
      `${API.v1}/${ProductDetailPath.Drawings}`,
      {
        params,
        context: withCache(),
      }
    );
  }

  getPricing(
    materialNumber: string,
    plant: string
  ): Observable<PricingResponse> {
    const params = new HttpParams()
      .set(this.PARAM_MATERIAL_NUMBER, materialNumber)
      .set(this.PARAM_PLANT, plant);

    return this.httpClient.get<PricingResponse>(
      `${API.v1}/${ProductDetailPath.Pricing}`,
      { params, context: withCache() }
    );
  }
}
